import React, { useContext, useMemo, useState } from "react";
import Tabs, { TabsMenuItem } from "../../../../Components/Tabs/Tabs";
import "./FiltersMenuPopup.scss";
import { FiltersContext } from "../../FilterProvider/FilterProvider";
import useRecentFilters from "../../useRecentFilters";
import { capitalize } from "lodash-es";
import { filterTabs } from "../../../AppViewUtils";
import Highlighter from "react-highlight-words";
import "./FilterSelection.scss";
import {
  Label,
  LabelVariant,
  Tooltip,
  Loader,
  IconButton,
  TextInput,
} from "@SeemplicityDev/shared";
import type { FilterObject } from "../../CommonFilterTypes";
import FilterPreviewPopover from "../../FilterPreview/FilterPreviewPopover";
import { filterTypes } from "../../../../Findings/FindingsUtils";

type FilterTab = (typeof filterTabs)[keyof typeof filterTabs];

// Extends the shared FilterObject with the popular/default meta flags read here
// and the `type` tag the component attaches to each filter row.
export interface FilterRow extends FilterObject {
  type?: FilterTab;
  meta?: FilterObject["meta"] & {
    popular?: boolean;
    default?: boolean;
  };
}

export interface FilterActionButton {
  title?: string;
  icon?: string;
  dataTestId?: string;
  onClick: (
    event: React.MouseEvent<HTMLButtonElement>,
    filter: FilterRow
  ) => void;
}

export interface FilterSelectionProps {
  menuHeader?: React.ReactNode;
  onSelect: (filter: FilterRow) => void;
  getActionButtons?: (filter: FilterRow) => FilterActionButton[];
  isSmall?: boolean;
  locallySelectedFilter?: FilterObject | null;
  showLabels?: boolean;
}

const FilterSelection: React.FC<FilterSelectionProps> = ({
  menuHeader,
  onSelect,
  getActionButtons,
  isSmall,
  locallySelectedFilter = null,
  showLabels = true,
}) => {
  const {
    filtersState: { filtersList, filtersMenuTab, selectedFilter },
    updateFiltersState,
  } = useContext(FiltersContext);
  const [searchValue, setSearchValue] = useState("");
  const { recentFilters } = useRecentFilters();

  const [selectedTab, setSelectedTab] = useState(filtersMenuTab);

  const menuItems = useMemo<TabsMenuItem[]>(() => {
    return [
      {
        pageLink: filterTabs.SAVED,
        title: "Saved",
        "data-analytics-event": "user_click",
        "data-analytics-props":
          '{"name": "saved_tab", "container": "filter_selection_dropdown"}',
      },
      {
        pageLink: filterTabs.RECOMMENDED,
        title: "Recommended",
        "data-analytics-event": "user_click",
        "data-analytics-props":
          '{"name": "recommended_tab", "container": "filter_selection_dropdown"}',
      },
      // {
      //   pageLink: filterTabs.RECENT,
      //   title: "Recent",
      // },
    ];
  }, []);

  const onSelectTab = (item: TabsMenuItem) => {
    updateFiltersState({ filtersMenuTab: item?.pageLink });
    setSelectedTab(item.pageLink as string);
  };

  const filters = useMemo<FilterRow[]>(() => {
    switch (selectedTab) {
      case filterTabs.SAVED: {
        return (filtersList as FilterRow[])
          ?.reduce<FilterRow[]>(
            (acc, filter) =>
              !filter?.meta?.system
                ? [...acc, { ...filter, type: filterTabs.SAVED }]
                : acc,
            []
          )
          .sort((a, b) => {
            return a?.label.localeCompare(b?.label);
          });
      }

      case filterTabs.RECENT: {
        return recentFilters?.map((filterData, index) => {
          return {
            label: index, //TODO: set name
            value: {
              query: filterData,
            },
          } as unknown as FilterRow;
        });
      }

      case filterTabs.RECOMMENDED: {
        return (filtersList as FilterRow[])
          ?.reduce<FilterRow[]>(
            (acc, filter) =>
              filter?.meta?.system
                ? [...acc, { ...filter, type: filterTabs.RECOMMENDED }]
                : acc,
            []
          )
          .sort((a, b) => {
            return (
              Number(b?.meta?.default) - Number(a?.meta?.default) ||
              Number(b?.meta?.popular) - Number(a?.meta?.popular) ||
              a?.label.localeCompare(b?.label)
            );
          });
      }

      default:
        return [];
    }
  }, [filtersList, selectedTab]);

  const displayedFilters = useMemo(
    () =>
      filters.filter((filter) =>
        filter.label?.toLowerCase()?.includes(searchValue.toLowerCase())
      ),
    [searchValue, filters]
  );

  const emptyStateSubtitle = useMemo(() => {
    switch (selectedTab) {
      case filterTabs.SAVED: {
        return "Try to create filters and save them";
      }

      case filterTabs.RECENT: {
        return "Try to use filters";
      }

      default:
        return "";
    }
  }, [selectedTab]);

  return (
    <div className={`filter-selection-container`}>
      {!!menuHeader && (
        <div className={`filters-menu-popup-header`}>{menuHeader}</div>
      )}
      <Tabs
        menuItems={menuItems}
        onSelectTab={onSelectTab}
        activeId={selectedTab}
      />
      <div className={"filter-search-input"}>
        <TextInput
          data-testid={"filter-search"}
          value={searchValue}
          placeholder={`Search ${selectedTab} Filters...`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e?.target?.value || "")
          }
          isClearable
          leftIcon={"seem-icon-search"}
          applyAutoFocus
        />
      </div>
      <div className={`filters-list-container`} data-testid="filter-list-table">
        {!!filters.length ? (
          displayedFilters?.length ? (
            displayedFilters?.map((filter, index) => {
              const actionButtons = getActionButtons?.(filter);
              const canEditFilter =
                !filter?.meta?.system && !!actionButtons?.length;
              const canPreviewFilter = !!filter?.value?.query;
              return (
                <div
                  key={`filter-menu-row-${index}`}
                  data-testid={`filter-menu-row-${index}`}
                  className={`filter-menu-row ${
                    locallySelectedFilter?.value?.id === filter?.value?.id ||
                    (!locallySelectedFilter &&
                      selectedFilter?.value?.id === filter?.value?.id)
                      ? "is-selected"
                      : ""
                  } ${
                    canEditFilter || canPreviewFilter ? "has-action-button" : ""
                  }`}
                  onClick={() => onSelect(filter)}
                >
                  <div className={`filter-name-wrapper`}>
                    <i
                      className={`seem-icon ${
                        selectedTab === filterTabs.SAVED
                          ? "seem-icon-filter"
                          : ""
                      } ${
                        selectedTab === filterTabs.RECOMMENDED
                          ? "seem-icon-filter-recommended"
                          : ""
                      }`}
                    />
                    <Tooltip content={filter.label}>
                      <div className={`filter-name`}>
                        <Highlighter
                          highlightClassName="highlight-text-wrap"
                          searchWords={[searchValue]}
                          autoEscape={true}
                          textToHighlight={`${filter.label}`}
                        />
                      </div>
                    </Tooltip>
                    {filter?.meta?.popular && showLabels && (
                      <Label
                        text={"POPULAR"}
                        variant={LabelVariant.Rect}
                        className="filter-popular-label"
                      />
                    )}
                  </div>
                  {(canEditFilter || canPreviewFilter) && (
                    <div className={`filter-action-buttons`}>
                      {canEditFilter &&
                        actionButtons?.map((actionButton, index) => (
                          <React.Fragment key={`action-button-${index}`}>
                            <IconButton
                              buttonStyle={"secondary"}
                              type="button"
                              data-testid={actionButton?.dataTestId}
                              isIcon={actionButton?.icon}
                              withTooltip={{ content: actionButton?.title }}
                              onClick={(e) => actionButton?.onClick(e, filter)}
                            />
                          </React.Fragment>
                        ))}
                      <FilterPreviewPopover
                        filterQuery={filter?.value?.query}
                        filterType={filterTypes.FILTER}
                      />
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <Loader
              flexcenter
              className={`no-filters-loader ${isSmall ? "is-small" : ""}`}
              icon={"seem-icon-filters-no-saved"}
              title={"No results"}
              iconSize={"xxxl"}
              subtitle={"Try adjusting your search"}
              fullHeight
            />
          )
        ) : (
          <Loader
            flexcenter
            icon={"seem-icon-filters-no-saved"}
            iconSize={"xxxl"}
            title={`No ${capitalize(selectedTab)} Filters`}
            subtitle={emptyStateSubtitle}
            className={`no-filters-loader ${isSmall ? "is-small" : ""}`}
            fullHeight
          />
        )}
      </div>
    </div>
  );
};

export default FilterSelection;
